import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { ClickAwayListener, Popper, ThemeProvider } from '@mui/material'

import Box from 'components_new/atoms/Box'

import { getTheme } from 'themes'

import { TypeIcon } from './ColumnLabel'
import { FieldOption } from './FieldSelect'
import { BOARD } from './accents'

interface ExpressionFieldProps {
  /** Raw expression with bare `<columnId>` reference tokens. */
  value: string
  /** Attributes available for insertion (upstream columns). */
  options: FieldOption[]
  onChange: (value: string) => void
  placeholder?: string
}

const HIT = {
  bg: '#DCFCE7',
  fg: BOARD.signalGreenDark
} as const

// The `@fragment` the caret is currently sitting at the end of, if any.
const FRAGMENT_RE = /@([\wÅÄÖåäö]*)$/

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const buildAlternation = (keys: string[]) =>
  [...keys]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|')

// Column ids may hold non-ASCII letters, which JavaScript's `\w` leaves out.
const WORD = '\\w\\u0080-\\uFFFF'

/** Alternation regex matching a bare `<key>`, capturing the char before it. */
const buildIdRegex = (keys: string[]) => {
  if (keys.length === 0) {
    return null
  }

  return new RegExp(
    `(^|[^${WORD}@])(${buildAlternation(keys)})(?![${WORD}])`,
    'g'
  )
}

/** Alternation regex matching `@<key>` for the given keys (longest first). */
const buildNameRegex = (keys: string[]) => {
  if (keys.length === 0) {
    return null
  }

  return new RegExp(`@(?:${buildAlternation(keys)})`, 'g')
}

const splitMatch = (name: string, query: string) => {
  const index = query ? name.toLowerCase().indexOf(query.toLowerCase()) : -1

  if (index < 0) {
    return { pre: name, hit: '', post: '' }
  }

  return {
    pre: name.slice(0, index),
    hit: name.slice(index, index + query.length),
    post: name.slice(index + query.length)
  }
}

const NodePill = (props: { index: number }) => (
  <Box
    component="span"
    sx={{
      alignItems: 'center',
      backgroundColor: BOARD.signalGreen,
      borderRadius: '4px',
      color: 'common.white',
      display: 'inline-grid',
      flex: 'none',
      fontFamily: BOARD.fontBody,
      fontSize: '9px',
      fontWeight: 700,
      height: '14px',
      minWidth: '14px',
      placeItems: 'center',
      px: '3px'
    }}
  >
    {props.index}
  </Box>
)

/**
 * Monospace expression input for the Calculate node. The stored value is the
 * engine contract (the Glue job's parse_expression): bare `<columnId>`
 * reference tokens and `_-_` for subtraction. The field renders a reference
 * as `@<name>` and `_-_` as `-`, mapping back on every edit. Typing `@` opens
 * an inline autocomplete with the shared field-selector look (grouped by
 * origin table + node, type icons, highlighted fragment) anchored to the
 * caret. Picking inserts `@<name> `.
 * Keyboard: ↑/↓ move, Enter inserts, Esc closes.
 */
const ExpressionField = (props: ExpressionFieldProps) => {
  const {
    value,
    options,
    onChange,
    placeholder = 'Skriv uttryck, @ för attribut'
  } = props

  const anchorRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const caretRef = useRef<number | null>(null)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [focused, setFocused] = useState(false)

  const idToName = useMemo(
    () => new Map(options.map((option) => [option.id, option.name])),
    [options]
  )
  const nameToId = useMemo(
    () => new Map(options.map((option) => [option.name, option.id])),
    [options]
  )

  const idRegex = useMemo(() => buildIdRegex([...idToName.keys()]), [idToName])
  const nameRegex = useMemo(
    () => buildNameRegex([...nameToId.keys()]),
    [nameToId]
  )

  // Stored `id` tokens and `_-_` → human `@name` and `-` for display, and
  // back on edit. Minus is swapped after names map to ids (and before ids map
  // to names) — ids never contain `-`, so at that point every `-` is an
  // operator.
  const toDisplay = useCallback(
    (text: string) => {
      const withMinus = text.replace(/_-_/g, '-')

      return idRegex
        ? withMinus.replace(idRegex, (match, delimiter: string, id: string) => {
          const name = idToName.get(id)

          return name ? `${delimiter}@${name}` : match
        })
        : withMinus
    },
    [idRegex, idToName]
  )
  const toInternal = useCallback(
    (text: string) => {
      const withIds = nameRegex
        ? text.replace(nameRegex, (match) => {
          const id = nameToId.get(match.slice(1))

          return id ?? match
        })
        : text

      return withIds.replace(/-/g, '_-_')
    },
    [nameRegex, nameToId]
  )

  const display = useMemo(() => toDisplay(value), [toDisplay, value])

  const matched = useMemo(() => {
    if (!query) {
      return options
    }

    const lower = query.toLowerCase()

    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(lower) ||
        (option.origin ?? '').toLowerCase().includes(lower)
    )
  }, [options, query])

  const groups = useMemo(() => {
    const byKey = new Map<string, FieldOption[]>()

    for (const option of matched) {
      const key = `${option.origin ?? ''}|${option.nodeIndex ?? ''}`
      const bucket = byKey.get(key)

      if (bucket) {
        bucket.push(option)
      } else {
        byKey.set(key, [option])
      }
    }

    return Array.from(byKey.values()).map((fields) => ({
      origin: fields[0].origin,
      nodeIndex: fields[0].nodeIndex,
      fields
    }))
  }, [matched])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, open])

  // Restore the caret after an insert re-renders the (transformed) value.
  useEffect(() => {
    if (caretRef.current !== null && inputRef.current) {
      const caret = caretRef.current

      inputRef.current.focus()
      inputRef.current.setSelectionRange(caret, caret)
      caretRef.current = null
    }
  }, [value])

  const syncFragment = (text: string, caret: number) => {
    const fragment = text.slice(0, caret).match(FRAGMENT_RE)

    if (fragment) {
      setQuery(fragment[1])
      setOpen(true)
    } else {
      setOpen(false)
      setQuery('')
    }
  }

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextDisplay = event.target.value
    const caret = event.target.selectionStart ?? nextDisplay.length

    onChange(toInternal(nextDisplay))
    syncFragment(nextDisplay, caret)
  }

  const handleInsert = useCallback(
    (option: FieldOption) => {
      const input = inputRef.current
      const caret = input?.selectionStart ?? display.length
      const before = display.slice(0, caret)
      const fragment = before.match(FRAGMENT_RE)
      const start = fragment ? before.length - fragment[0].length : caret
      const nextBefore = `${display.slice(0, start)}@${option.name} `
      const nextDisplay = nextBefore + display.slice(caret)

      caretRef.current = nextBefore.length
      onChange(toInternal(nextDisplay))
      setOpen(false)
      setQuery('')
    },
    [display, onChange, toInternal]
  )

  const flat = matched
  const activeOption = flat[activeIndex]

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open || flat.length === 0) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, flat.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()

      if (activeOption) {
        handleInsert(activeOption)
      }
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    }
  }

  let rowIndex = -1

  return (
    <Box ref={anchorRef} sx={{ position: 'relative' }}>
      <input
        className="nodrag ignore-locked-mode"
        ref={inputRef}
        onBlur={() => setFocused(false)}
        onChange={handleInput}
        onFocus={() => setFocused(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        value={display}
        style={{
          background: focused ? '#ffffff' : BOARD.rowBackground,
          border: `1px solid ${focused ? BOARD.signalGreen : 'transparent'}`,
          borderRadius: '10px',
          boxSizing: 'border-box',
          color: BOARD.signalGreenDark,
          fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: '13px',
          outline: 'none',
          padding: '10px 11px',
          width: '100%'
        }}
      />

      <Popper
        anchorEl={anchorRef.current}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        open={open && flat.length > 0}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            onMouseDown={(event) => event.preventDefault()}
            sx={{ width: 300 }}
          >
            <ThemeProvider theme={getTheme('light')}>
              <Box
                sx={{
                  backgroundColor: 'common.white',
                  border: `1px solid ${BOARD.border}`,
                  borderRadius: '13px',
                  boxShadow: '0 14px 34px rgba(16, 24, 40, 0.16)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    color: BOARD.textFaint,
                    fontFamily: BOARD.fontDisplay,
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    p: '8px 12px 5px',
                    textTransform: 'uppercase'
                  }}
                >
                  Infoga attribut
                </Box>
                <Box sx={{ maxHeight: 220, overflowY: 'auto', pb: 0.75 }}>
                  {groups.map((group) => (
                    <Box key={`${group.origin ?? ''}|${group.nodeIndex ?? ''}`}>
                      {group.origin || group.nodeIndex ? (
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '5px',
                            p: '6px 12px 3px'
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: BOARD.textFaint,
                              fontFamily: BOARD.fontDisplay,
                              fontSize: '10px',
                              fontWeight: 700,
                              letterSpacing: '0.06em',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {group.origin ?? 'Okänd källa'}
                          </Box>
                          {group.nodeIndex ? (
                            <NodePill index={group.nodeIndex} />
                          ) : null}
                        </Box>
                      ) : null}
                      {group.fields.map((option) => {
                        rowIndex += 1
                        const currentIndex = rowIndex
                        const { pre, hit, post } = splitMatch(
                          option.name,
                          query
                        )

                        return (
                          <Box
                            key={option.id}
                            onClick={() => handleInsert(option)}
                            onMouseEnter={() => setActiveIndex(currentIndex)}
                            sx={{
                              alignItems: 'center',
                              backgroundColor:
                                activeIndex === currentIndex
                                  ? BOARD.chipBackground
                                  : 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              gap: 1,
                              p: '7px 12px'
                            }}
                          >
                            <TypeIcon type={option.type} />
                            <Box
                              sx={{
                                color: BOARD.textPrimary,
                                flex: 1,
                                fontFamily: BOARD.fontBody,
                                fontSize: '13px',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {pre}
                              {hit ? (
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: HIT.bg,
                                    borderRadius: '3px',
                                    color: HIT.fg,
                                    fontWeight: 700
                                  }}
                                >
                                  {hit}
                                </Box>
                              ) : null}
                              {post}
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            </ThemeProvider>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
}

export default ExpressionField
